import React, { createContext, useContext, useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabaseClient';
import { getSubscription, extractTier } from '../lib/billing';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionPlan, setSubscriptionPlan] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);
  const subscriptionRequestRef = useRef(0);

  const fetchSubscription = useCallback(async (currentSession) => {
    const activeUser = currentSession?.user;
    if (!activeUser) {
      setSubscriptionPlan(null);
      setSubscriptionLoading(false);
      return;
    }

    const requestId = ++subscriptionRequestRef.current;
    setSubscriptionLoading(true);

    try {
      const data = await getSubscription();

      // Ignore stale results if auth state changed while request was in flight.
      if (requestId !== subscriptionRequestRef.current) return;

      const tier = extractTier(data);
      setSubscriptionPlan(tier);
      setSubscriptionLoading(false);
    } catch (error) {
      if (requestId !== subscriptionRequestRef.current) return;

      console.error("AuthProvider: Error fetching subscription:", error);
      const metadataTier = extractTier(activeUser?.user_metadata?.subscription_tier);
      setSubscriptionPlan(metadataTier);
      setSubscriptionLoading(false);
    }
  }, []);

  // Update subscriptionPlan when the signed-in user changes.
  useEffect(() => {
    if (session?.user) {
      fetchSubscription(session);
    } else {
      subscriptionRequestRef.current += 1;
      setSubscriptionPlan(null);
      setSubscriptionLoading(false);
    }
  }, [session?.user, fetchSubscription]);

  useEffect(() => {
    // Initial session check
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      setLoading(false);
    }).catch((error) => {
      console.error("AuthProvider: Error getting session:", error);
      setSession(null);
      setUser(null);
      setLoading(false);
    });

    // Auth state changes
    const { data } = supabase.auth.onAuthStateChange((_event, currentSession) => {
      setSession(currentSession);
      setUser(currentSession?.user ?? null);
      setLoading(false);
    });

    return () => {
      if (data && data.subscription) {
        data.subscription.unsubscribe();
      }
    };
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const refreshSubscription = useCallback(() => fetchSubscription(session), [fetchSubscription, session]);

  const value = useMemo(() => ({
    session,
    user,
    signOut,
    loading,
    subscriptionPlan,
    subscriptionLoading,
    refreshSubscription,
  }), [session, user, signOut, loading, subscriptionPlan, subscriptionLoading, refreshSubscription]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
